import React from 'react';
import { Helmet } from 'react-helmet-async';

const CANONICAL_HOST = 'https://www.aakeeo.com';
const DEFAULT_OG_IMAGE = CANONICAL_HOST + '/logo.png';

const SEO = ({ title, description, canonical, keywords, og = {}, twitter = {}, jsonLd, breadcrumbs }) => {
  // Normalize canonical: swap any non-aakeeo origin for the canonical host so
  // prerendered HTML doesn't leak localhost URLs.
  let canonicalUrl = canonical;
  if (typeof window !== 'undefined') {
    if (!canonicalUrl) {
      canonicalUrl = CANONICAL_HOST + window.location.pathname;
    } else if (canonicalUrl.startsWith('http://') || canonicalUrl.startsWith('https://')) {
      try {
        const parsed = new URL(canonicalUrl);
        if (parsed.host !== 'www.aakeeo.com') {
          canonicalUrl = CANONICAL_HOST + parsed.pathname + parsed.search;
        }
      } catch (e) { /* ignore */ }
    }
  } else if (!canonicalUrl) {
    canonicalUrl = CANONICAL_HOST;
  }

  const ogTitle = og.title || title;
  const ogDesc = og.description || description;
  const ogUrl = og.url || canonicalUrl;
  const ogType = og.type || 'website';

  let ogImage = og.image;
  if (ogImage && ogImage.startsWith('/')) {
    ogImage = CANONICAL_HOST + ogImage;
  } else if (ogImage && typeof window !== 'undefined' && ogImage.startsWith(window.location.origin)) {
    ogImage = ogImage.replace(window.location.origin, CANONICAL_HOST);
  }
  // Fall back to a site-wide default so every page has at least a brand image
  if (!ogImage) ogImage = DEFAULT_OG_IMAGE;

  // Build all JSON-LD schemas into a single array
  const schemas = [];

  if (jsonLd) {
    if (Array.isArray(jsonLd)) {
      schemas.push(...jsonLd);
    } else {
      schemas.push(jsonLd);
    }
  }

  // Auto-generate BreadcrumbList schema from breadcrumbs prop
  if (breadcrumbs && breadcrumbs.length > 0) {
    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      'itemListElement': breadcrumbs.map((crumb, i) => ({
        '@type': 'ListItem',
        'position': i + 1,
        'name': crumb.name,
        ...(crumb.url ? { 'item': CANONICAL_HOST + crumb.url } : {})
      }))
    });
  }

  return (
    <Helmet>
      {/* Standard Metadata */}
      {title && <title>{title}</title>}
      {description && <meta name="description" content={description} />}
      {keywords && <meta name="keywords" content={Array.isArray(keywords) ? keywords.join(', ') : keywords} />}

      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph */}
      {ogTitle && <meta property="og:title" content={ogTitle} />}
      {ogDesc && <meta property="og:description" content={ogDesc} />}
      {ogUrl && <meta property="og:url" content={ogUrl} />}
      <meta property="og:type" content={ogType} />
      {ogImage && <meta property="og:image" content={ogImage} />}

      {/* Twitter */}
      <meta name="twitter:card" content={twitter.card || (ogImage ? 'summary_large_image' : 'summary')} />
      {(twitter.title || title) && <meta name="twitter:title" content={twitter.title || title} />}
      {(twitter.description || description) && <meta name="twitter:description" content={twitter.description || description} />}
      {(twitter.image || ogImage) && <meta name="twitter:image" content={twitter.image || ogImage} />}

      {/* Structured Data */}
      {schemas.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
